import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  getTimeSlots,
  getTimeSlotsByDate,
  createBooking,
  createRequest,
  createTimeSlot,
  createMultipleTimeSlots,
  deleteTimeSlot,
  deleteAllTimeSlots,
  getAllBookings,
  getRequests,
  updateRequestStatus,
  deleteRequest,
  cancelBooking,
  getAllHostAvailability,
  setHostAvailability as updateHostAvailability,
  getAvailableHostsForSlot,
  getBookingsByHostForSlot,
  addCurrentUserToAllSlots,
  diagnoseAvailabilityIdMismatches,
  migrateAvailabilityIds,
  deleteOrphanedAvailability
} from '../firebase/appointmentService';
import { getSessionHosts, getUsers } from '../firebase/userService';
import { sendBookingNotification, formatTimeForEmail, formatDateForEmail } from '../firebase/emailService';
import { format, addMonths, startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, isSameDay, isSameMonth, parseISO } from 'date-fns';
import { isOfficerOrVarsity } from '../utils/permissions';
import { parseDateLocal } from '../utils/dateUtils';
import './Appointments.css';

// Helper function to convert military time to 12-hour format with AM/PM
const formatTime = (time) => {
  if (!time) return '';
  const [hours, minutes] = time.split(':');
  const hour = parseInt(hours, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour % 12 || 12;
  return `${displayHour}:${minutes} ${ampm}`;
};

function Appointments() {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('book');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [timeSlots, setTimeSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [bookingForm, setBookingForm] = useState({
    name: '',
    email: currentUser?.email || '',
    reason: ''
  });
  const [requestForm, setRequestForm] = useState({
    name: '',
    email: currentUser?.email || '',
    date: '',
    time: '',
    reason: ''
  });
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showAdminModal, setShowAdminModal] = useState(false);
  const [newSlotForm, setNewSlotForm] = useState({
    date: '',
    startTime: '',
    endTime: '',
    capacity: 1,
    notes: '',
    isRepeating: false,
    endDate: '',
    daysOfWeek: {
      sunday: false,
      monday: false,
      tuesday: false,
      wednesday: false,
      thursday: false,
      friday: false,
      saturday: false
    }
  });
  const [allBookings, setAllBookings] = useState([]);
  const [allRequests, setAllRequests] = useState([]);
  const [showDateModal, setShowDateModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancellingBooking, setCancellingBooking] = useState(null);
  const [cancellationNote, setCancellationNote] = useState('');
  const [filterMonth, setFilterMonth] = useState(new Date());
  const [filterTime, setFilterTime] = useState('all');
  const [filterWeek, setFilterWeek] = useState('all');

  // Session Hosts state
  const [hosts, setHosts] = useState([]);
  const [hostAvailability, setHostAvailability] = useState([]);
  const [selectedHost, setSelectedHost] = useState(null);
  const [availableHostsMap, setAvailableHostsMap] = useState({});
  const [currentUserData, setCurrentUserData] = useState(null);
  const [mySessionsBookings, setMySessionsBookings] = useState([]);

  // My Availability state
  const [myAvailability, setMyAvailability] = useState(new Set());
  const [savingAvailability, setSavingAvailability] = useState(false);

  // Diagnostic state
  const [diagnosticResults, setDiagnosticResults] = useState(null);
  const [runningDiagnostic, setRunningDiagnostic] = useState(false);
  const [runningMigration, setRunningMigration] = useState(false);
  const [deletingOrphaned, setDeletingOrphaned] = useState(false);
  const [allUsers, setAllUsers] = useState([]);

  // Host availability calendar navigation
  const [currentAvailabilityWeek, setCurrentAvailabilityWeek] = useState(new Date());

  useEffect(() => {
    const checkAdmin = async () => {
      loadTimeSlots();
      loadAllBookings(); // Load bookings for all users to show on cards
      loadHosts();
      loadHostAvailability();

      // Check if user is admin (hardcoded emails or Admin role)
      const adminEmails = ['kaseywilleby@gmail.com', 'mr.willeby@gmail.com'];
      let isAdminUser = currentUser?.email && adminEmails.map(e => e.toLowerCase()).includes(currentUser.email.toLowerCase());

      // Also check if user has Admin role and get their user data
      if (currentUser?.email) {
        const usersResult = await getUsers();
        if (usersResult.success) {
          setAllUsers(usersResult.users); // Store all users for diagnostic
          const userData = usersResult.users.find(u => u.email?.toLowerCase() === currentUser.email.toLowerCase());
          setCurrentUserData(userData);
          if (!isAdminUser && userData && userData.role === 'Admin') {
            isAdminUser = true;
          }
        }
      }

      if (isAdminUser) {
        setIsAdmin(true);
        loadAllRequests();
      } else {
        setIsAdmin(false);
      }
    };

    checkAdmin();
  }, [currentUser]);

  // Update my sessions when user data or bookings change
  useEffect(() => {
    if (currentUserData && allBookings.length > 0) {
      filterMySessionsBookings(allBookings);
    }
  }, [currentUserData, allBookings]);

  // Reload availability when currentUserData is loaded
  useEffect(() => {
    if (currentUserData && hostAvailability.length > 0) {
      // Reload user's availability now that we have their Firestore ID
      const userFirestoreId = currentUserData.id;
      const userAuthUid = currentUser.uid || currentUser.id;

      console.log('Reloading user availability with userData:', { userFirestoreId, userAuthUid });

      const myAvail = hostAvailability
        .filter(a => {
          const matches = (a.hostId === userFirestoreId || a.hostId === userAuthUid) && a.isAvailable;
          if (matches) {
            console.log('Found availability match:', a);
          }
          return matches;
        })
        .map(a => a.slotId);

      console.log('Updated my availability slots:', myAvail);
      setMyAvailability(new Set(myAvail));
    }
  }, [currentUserData, hostAvailability, currentUser]);

  const loadTimeSlots = async () => {
    setLoading(true);
    const result = await getTimeSlots();
    if (result.success) {
      // Filter out past time slots (checking both date and time)
      const now = new Date();
      const today = format(now, 'yyyy-MM-dd');
      const currentTime = format(now, 'HH:mm');

      const futureSlots = result.slots.filter(slot => {
        // If the date is in the future, include it
        if (slot.date > today) return true;
        // If the date is today, check if the end time hasn't passed yet
        if (slot.date === today) {
          return slot.endTime > currentTime;
        }
        // If the date is in the past, exclude it
        return false;
      });
      setTimeSlots(futureSlots);
    }
    setLoading(false);
  };

  const handleDateClick = (date) => {
    setSelectedDate(date);
    setShowDateModal(true);
  };

  const getSlotsForDate = (date) => {
    if (!date) return [];
    const dateStr = format(date, 'yyyy-MM-dd');
    return timeSlots.filter(slot => slot.date === dateStr);
  };

  const getSlotsForMonth = (date) => {
    const monthStart = startOfMonth(date);
    const monthEnd = endOfMonth(date);
    const startDateStr = format(monthStart, 'yyyy-MM-dd');
    const endDateStr = format(monthEnd, 'yyyy-MM-dd');

    let filtered = timeSlots
      .filter(slot => slot.date >= startDateStr && slot.date <= endDateStr);

    // Apply week filter
    if (filterWeek !== 'all') {
      const weekNum = parseInt(filterWeek, 10);
      const firstDayOfMonth = startOfMonth(date);
      const firstMonday = startOfWeek(firstDayOfMonth, { weekStartsOn: 1 });

      // Calculate the start and end of the selected week
      const weekStartDate = addDays(firstMonday, (weekNum - 1) * 7);
      const weekEndDate = addDays(weekStartDate, 6);
      const weekStartStr = format(weekStartDate, 'yyyy-MM-dd');
      const weekEndStr = format(weekEndDate, 'yyyy-MM-dd');

      filtered = filtered.filter(slot => slot.date >= weekStartStr && slot.date <= weekEndStr);
    }

    // Apply time filter
    if (filterTime !== 'all') {
      filtered = filtered.filter(slot => {
        const [hour, minute] = slot.startTime.split(':').map(Number);
        const timeInMinutes = hour * 60 + minute;

        // 7th period: 1:43-2:35 (13:43-14:35)
        if (filterTime === 'period7') {
          return timeInMinutes >= 13 * 60 + 43 && timeInMinutes < 14 * 60 + 35;
        }
        // 2:45-3:30 (14:45-15:30)
        if (filterTime === 'slot1') {
          return timeInMinutes >= 14 * 60 + 45 && timeInMinutes < 15 * 60 + 30;
        }
        // 3:30-4:15 (15:30-16:15)
        if (filterTime === 'slot2') {
          return timeInMinutes >= 15 * 60 + 30 && timeInMinutes < 16 * 60 + 15;
        }
        // 4:15-5:00 PM (16:15-17:00)
        if (filterTime === 'slot3') {
          return timeInMinutes >= 16 * 60 + 15 && timeInMinutes < 17 * 60;
        }
        return true;
      });
    }

    return filtered.sort((a, b) => {
      if (a.date !== b.date) {
        return a.date.localeCompare(b.date);
      }
      return a.startTime.localeCompare(b.startTime);
    });
  };

  const getWeeksInMonth = (date) => {
    const monthStart = startOfMonth(date);
    const monthEnd = endOfMonth(date);
    const firstMonday = startOfWeek(monthStart, { weekStartsOn: 1 });

    const weeks = [];
    let currentWeekStart = firstMonday;
    let weekNumber = 1;

    while (currentWeekStart <= monthEnd || currentWeekStart < addDays(monthEnd, 7)) {
      const weekEnd = addDays(currentWeekStart, 6);

      // Check if this week overlaps with the month
      if (weekEnd >= monthStart && currentWeekStart <= monthEnd) {
        weeks.push({
          number: weekNumber,
          start: currentWeekStart,
          end: weekEnd,
          label: `Week ${weekNumber} (${format(currentWeekStart, 'MMM d')} - ${format(weekEnd, 'MMM d')})`
        });
      }

      currentWeekStart = addDays(currentWeekStart, 7);
      weekNumber++;

      // Safety break after 6 weeks
      if (weekNumber > 6) break;
    }

    return weeks;
  };

  const handleSlotCardClick = (slot) => {
    if (isAdmin) return; // Admins don't book from cards
    handleSlotSelect(slot);
  };

  const handleSlotSelect = (slot) => {
    // Check if there are any unbooked hosts available
    const unbookedHosts = getAvailableUnbookedHosts(slot.id);
    const availableSpots = unbookedHosts.length > 0 ? unbookedHosts.length : slot.capacity - (slot.bookingCount || 0);

    if (availableSpots <= 0) {
      alert('This time slot is fully booked. Please select another time.');
      return;
    }

    setSelectedSlot(slot);
    setShowBookingForm(true);
  };

  const handleBookingSubmit = async (e) => {
    e.preventDefault();
    if (!selectedSlot) return;

    // Check if host selection is required
    const slotHosts = availableHostsMap[selectedSlot.id] || [];
    if (slotHosts.length > 0 && !selectedHost) {
      alert('Please select a session host');
      return;
    }

    const selectedHostData = selectedHost ? hosts.find(h => h.id === selectedHost) : null;

    const result = await createBooking({
      ...bookingForm,
      slotId: selectedSlot.id,
      date: selectedSlot.date,
      startTime: selectedSlot.startTime,
      endTime: selectedSlot.endTime,
      hostId: selectedHost || null,
      hostName: selectedHostData?.name || null
    });

    if (result.success) {
      // Send email notification to the selected host
      if (selectedHostData && selectedHostData.email) {
        const emailResult = await sendBookingNotification({
          hostEmail: selectedHostData.email,
          hostName: selectedHostData.name,
          studentName: bookingForm.name,
          studentEmail: bookingForm.email,
          date: formatDateForEmail(selectedSlot.date),
          startTime: formatTimeForEmail(selectedSlot.startTime),
          endTime: formatTimeForEmail(selectedSlot.endTime),
          reason: bookingForm.reason
        });

        if (!emailResult.success) {
          console.warn('Failed to send email notification:', emailResult.error);
          // Don't fail the booking if email fails
        }
      }

      const roomMessage = result.room ? `\n\nYou have been assigned to Room ${result.room}.` : '';
      alert('Booking confirmed!' + roomMessage + ' ' + (selectedHostData ? `${selectedHostData.name} has been notified.` : ''));
      setShowBookingForm(false);
      setSelectedSlot(null);
      setSelectedHost(null);
      setBookingForm({ name: '', email: currentUser?.email || '', reason: '' });
      loadTimeSlots();
      loadAllBookings();
    } else {
      alert(result.error || 'Booking failed. Please try again.');
    }
  };

  const handleRequestSubmit = async (e) => {
    e.preventDefault();

    const result = await createRequest(requestForm);

    if (result.success) {
      alert('Request submitted! We will contact you soon.');
      setRequestForm({ name: '', email: currentUser?.email || '', date: '', time: '', reason: '' });
    } else {
      alert('Request failed. Please try again.');
    }
  };

  const handleCreateSlot = async (e) => {
    e.preventDefault();

    // Get current user's ID to automatically add them as available
    const adminUserId = currentUser?.uid;

    if (!newSlotForm.isRepeating) {
      // Single slot creation
      const { isRepeating, endDate, daysOfWeek, ...slotData } = newSlotForm;
      const result = await createTimeSlot(slotData, adminUserId);

      if (result.success) {
        alert('Time slot created! You have been automatically marked as available.');
        setShowAdminModal(false);
        setNewSlotForm({
          date: '',
          startTime: '',
          endTime: '',
          capacity: 1,
          notes: '',
          isRepeating: false,
          endDate: '',
          daysOfWeek: {
            sunday: false,
            monday: false,
            tuesday: false,
            wednesday: false,
            thursday: false,
            friday: false,
            saturday: false
          }
        });
        loadTimeSlots();
        loadHostAvailability(); // Reload to show the admin as available
      } else {
        alert('Failed to create time slot.');
      }
    } else {
      // Multiple slots creation
      const slots = generateRepeatingSlots();

      if (slots.length === 0) {
        alert('Please select at least one day of the week.');
        return;
      }

      const result = await createMultipleTimeSlots(slots, adminUserId);

      if (result.success) {
        alert(`${result.count} time slots created! You have been automatically marked as available for all slots.`);
        setShowAdminModal(false);
        setNewSlotForm({
          date: '',
          startTime: '',
          endTime: '',
          capacity: 1,
          notes: '',
          isRepeating: false,
          endDate: '',
          daysOfWeek: {
            sunday: false,
            monday: false,
            tuesday: false,
            wednesday: false,
            thursday: false,
            friday: false,
            saturday: false
          }
        });
        loadTimeSlots();
        loadHostAvailability(); // Reload to show the admin as available
      } else {
        alert('Failed to create time slots.');
      }
    }
  };

  const generateRepeatingSlots = () => {
    const slots = [];
    const startDate = new Date(newSlotForm.date);
    const endDate = new Date(newSlotForm.endDate);

    // Map day names to day numbers (0 = Sunday, 6 = Saturday)
    const dayMap = {
      sunday: 0,
      monday: 1,
      tuesday: 2,
      wednesday: 3,
      thursday: 4,
      friday: 5,
      saturday: 6
    };

    // Get selected day numbers
    const selectedDays = Object.keys(newSlotForm.daysOfWeek)
      .filter(day => newSlotForm.daysOfWeek[day])
      .map(day => dayMap[day]);

    // Iterate through each day in the range
    let currentDate = new Date(startDate);
    while (currentDate <= endDate) {
      const dayOfWeek = currentDate.getDay();

      // If this day is selected, create a slot
      if (selectedDays.includes(dayOfWeek)) {
        slots.push({
          date: format(currentDate, 'yyyy-MM-dd'),
          startTime: newSlotForm.startTime,
          endTime: newSlotForm.endTime,
          capacity: 1,
          notes: newSlotForm.notes
        });
      }

      // Move to next day
      currentDate = addDays(currentDate, 1);
    }

    return slots;
  };

  const handleDeleteSlot = async (slotId) => {
    if (window.confirm('Are you sure you want to delete this time slot?')) {
      const result = await deleteTimeSlot(slotId);
      if (result.success) {
        loadTimeSlots();
      }
    }
  };

  const handleClearAllSlots = async () => {
    if (window.confirm('⚠️ WARNING: This will delete ALL time slots and cannot be undone. Are you absolutely sure?')) {
      const result = await deleteAllTimeSlots();
      if (result.success) {
        alert(`Successfully deleted ${result.count} time slots`);
        loadTimeSlots();
        loadHostAvailability();
      } else {
        alert('Failed to delete time slots');
      }
    }
  };

  const loadAllBookings = async () => {
    const result = await getAllBookings();
    if (result.success) {
      setAllBookings(result.bookings);
      // Also update my sessions if applicable
      if (currentUserData) {
        filterMySessionsBookings(result.bookings);
      }
    }
  };

  const filterMySessionsBookings = (bookings) => {
    if (!currentUser || !currentUserData) return;

    const now = new Date();
    const today = format(now, 'yyyy-MM-dd');
    const currentTime = format(now, 'HH:mm');

    // Filter bookings where user is either the booker OR the host
    const mySessions = bookings.filter(booking => {
      // Check if user is the booker (by email)
      const isBooker = booking.email?.toLowerCase() === currentUser.email?.toLowerCase();

      // Check if user is the host (by hostId)
      const isHost = booking.hostId === currentUserData.id;

      return isBooker || isHost;
    });

    // Sort by date and time (most recent first for upcoming, reverse chronological for past)
    const sortedSessions = mySessions.sort((a, b) => {
      if (a.date !== b.date) return a.date.localeCompare(b.date);
      return a.startTime.localeCompare(b.startTime);
    });

    // Separate into upcoming and past
    const upcomingSessions = sortedSessions.filter(booking => {
      if (booking.date > today) return true;
      if (booking.date === today) return booking.endTime > currentTime;
      return false;
    });

    const pastSessions = sortedSessions.filter(booking => {
      if (booking.date < today) return true;
      if (booking.date === today) return booking.endTime <= currentTime;
      return false;
    }).reverse(); // Reverse to show most recent past sessions first

    // Combine: upcoming sessions first, then past sessions
    setMySessionsBookings([...upcomingSessions, ...pastSessions]);
  };

  const loadAllRequests = async () => {
    const result = await getRequests();
    if (result.success) {
      setAllRequests(result.requests);
    }
  };

  const loadHosts = async () => {
    const result = await getSessionHosts();
    if (result.success) {
      setHosts(result.users); // getSessionHosts returns 'users' not 'hosts'
    }
  };

  const loadHostAvailability = async () => {
    const result = await getAllHostAvailability();
    if (result.success) {
      setHostAvailability(result.availability);
      // Build map of slot -> available hosts
      const map = {};
      result.availability.forEach(avail => {
        if (avail.isAvailable) {
          if (!map[avail.slotId]) {
            map[avail.slotId] = [];
          }
          map[avail.slotId].push(avail.hostId);
        }
      });
      setAvailableHostsMap(map);

      // Load current user's availability
      // IMPORTANT: Check both Firestore document ID and Auth UID for backwards compatibility
      if (currentUser) {
        const userFirestoreId = currentUserData?.id;
        const userAuthUid = currentUser.uid || currentUser.id;

        console.log('Loading user availability for:', { userFirestoreId, userAuthUid });

        const myAvail = result.availability
          .filter(a => {
            // Match either Firestore ID or Auth UID for backwards compatibility
            const matches = (a.hostId === userFirestoreId || a.hostId === userAuthUid) && a.isAvailable;
            if (matches) {
              console.log('Found availability match:', a);
            }
            return matches;
          })
          .map(a => a.slotId);

        console.log('My availability slots:', myAvail);
        setMyAvailability(new Set(myAvail));
      }
    }
  };

  // Host management is now handled in the User Management page

  const handleToggleHostAvailability = async (hostId, slotId, currentlyAvailable) => {
    try {
      const result = await updateHostAvailability(hostId, slotId, !currentlyAvailable);
      if (result.success) {
        await loadHostAvailability();
      } else {
        console.error('Failed to toggle availability:', result.error);
      }
    } catch (error) {
      console.error('Error toggling availability:', error);
    }
  };

  const handleToggleMyAvailability = async (slotId) => {
    try {
      setSavingAvailability(true);

      // CRITICAL: Must have Firestore document ID to ensure consistency with calendar display
      if (!currentUserData?.id) {
        console.error('Cannot toggle availability: User data not loaded yet');
        alert('Please wait for user data to load and try again.');
        setSavingAvailability(false);
        return;
      }

      const hostId = currentUserData.id; // ONLY use Firestore document ID
      const isCurrentlyAvailable = myAvailability.has(slotId);
      const newAvailability = !isCurrentlyAvailable;

      console.log('Toggling availability:', {
        hostId,
        slotId,
        isCurrentlyAvailable,
        newAvailability,
        currentUserData: currentUserData?.name,
        firestoreId: currentUserData?.id,
        authUid: currentUser.uid
      });

      // Optimistically update UI immediately
      const newSet = new Set(myAvailability);
      if (newAvailability) {
        newSet.add(slotId);
      } else {
        newSet.delete(slotId);
      }
      setMyAvailability(newSet);

      const result = await updateHostAvailability(hostId, slotId, newAvailability);

      console.log('Update result:', result);

      if (result.success) {
        console.log('Availability updated successfully - no page reload needed');
        // Don't reload - the optimistic update already updated the UI
        // The next natural data refresh will pick up any changes
      } else {
        console.error('Failed to update availability:', result.error);
        // Revert the optimistic update
        setMyAvailability(myAvailability);
        alert('Error updating availability. Please try again.');
      }
    } catch (error) {
      console.error('Error in handleToggleMyAvailability:', error);
      // Revert the optimistic update
      setMyAvailability(myAvailability);
      alert('Error updating availability. Please try again.');
    } finally {
      setSavingAvailability(false);
    }
  };

  const groupSlotsByDate = () => {
    const grouped = {};
    timeSlots.forEach(slot => {
      const dateKey = slot.date;
      if (!grouped[dateKey]) {
        grouped[dateKey] = {
          date: parseDateLocal(slot.date),
          slots: []
        };
      }
      grouped[dateKey].slots.push(slot);
    });

    // Sort slots by time within each date
    Object.values(grouped).forEach(group => {
      group.slots.sort((a, b) => a.startTime.localeCompare(b.startTime));
    });

    return Object.values(grouped).sort((a, b) => a.date - b.date);
  };

  const handleApproveRequest = async (requestId) => {
    const result = await updateRequestStatus(requestId, 'approved');
    if (result.success) {
      alert('Request approved!');
      loadAllRequests();
    }
  };

  const handleDenyRequest = async (requestId) => {
    if (window.confirm('Are you sure you want to deny this request?')) {
      const result = await updateRequestStatus(requestId, 'denied');
      if (result.success) {
        loadAllRequests();
      }
    }
  };

  const handleDeleteRequest = async (requestId) => {
    if (window.confirm('Are you sure you want to delete this request?')) {
      const result = await deleteRequest(requestId);
      if (result.success) {
        loadAllRequests();
      }
    }
  };

  const getBookingsForSlot = (slotId) => {
    return allBookings.filter(booking => booking.slotId === slotId && booking.status !== 'cancelled');
  };

  // Get available (not yet booked) hosts for a specific slot
  const getAvailableUnbookedHosts = (slotId) => {
    const slotHosts = hosts.filter(h => availableHostsMap[slotId]?.includes(h.id));
    const slotBookings = getBookingsForSlot(slotId);

    // Get list of host IDs that are already booked (have active bookings)
    const bookedHostIds = slotBookings
      .map(b => b.hostId)
      .filter(Boolean); // Remove null/undefined values

    // Return only hosts who are not yet booked
    return slotHosts.filter(host => !bookedHostIds.includes(host.id));
  };

  // Get booked hosts for a specific slot
  const getBookedHosts = (slotId) => {
    const slotBookings = getBookingsForSlot(slotId);
    const bookedHostIds = slotBookings
      .map(b => b.hostId)
      .filter(Boolean); // Remove null/undefined values

    // Return hosts who are booked
    return hosts.filter(host => bookedHostIds.includes(host.id));
  };

  const handleCancelBookingClick = (booking) => {
    setCancellingBooking(booking);
    setShowCancelModal(true);
  };

  const handleConfirmCancel = async () => {
    if (!cancellingBooking) return;

    const result = await cancelBooking(cancellingBooking.id, cancellingBooking.slotId, cancellationNote);
    if (result.success) {
      // Send cancellation email notification
      try {
        const isUserHost = cancellingBooking.hostId === currentUserData?.id;
        const isUserClient = cancellingBooking.email?.toLowerCase() === currentUser?.email?.toLowerCase();

        if (isUserHost || isUserClient) {
          // Find the host details
          const bookingHost = cancellingBooking.hostId ? hosts.find(h => h.id === cancellingBooking.hostId) : null;

          // Determine who to notify
          let recipientEmail = '';
          let recipientName = '';
          let cancelledBy = '';

          if (isUserHost) {
            // Host cancelled - notify the client
            recipientEmail = cancellingBooking.email;
            recipientName = cancellingBooking.name;
            cancelledBy = bookingHost?.name || 'the host';
          } else if (isUserClient) {
            // Client cancelled - notify the host
            if (bookingHost && bookingHost.email) {
              recipientEmail = bookingHost.email;
              recipientName = bookingHost.name;
              cancelledBy = cancellingBooking.name;
            }
          }

          if (recipientEmail) {
            const emailData = {
              to: recipientEmail,
              subject: 'Session Cancelled',
              html: `
                <h2>Session Cancelled</h2>
                <p>Hello ${recipientName},</p>
                <p>${cancelledBy} has cancelled the following session:</p>
                <div style="background: #f8f9fa; padding: 1rem; border-radius: 8px; margin: 1rem 0;">
                  <p><strong>Date:</strong> ${formatDateForEmail(cancellingBooking.date)}</p>
                  <p><strong>Time:</strong> ${formatTimeForEmail(cancellingBooking.startTime)} - ${formatTimeForEmail(cancellingBooking.endTime)}</p>
                  ${cancellingBooking.reason ? `<p><strong>Original Reason:</strong> ${cancellingBooking.reason}</p>` : ''}
                  ${cancellationNote ? `<p><strong>Cancellation Note:</strong> ${cancellationNote}</p>` : ''}
                </div>
                <p>If you have any questions, please reach out.</p>
                <p>Best regards,<br>Mr. Willeby's Tutorial Booking System</p>
              `
            };

            await sendBookingNotification(emailData);
          }
        }
      } catch (emailError) {
        console.error('Failed to send cancellation email:', emailError);
        // Don't fail the cancellation if email fails
      }

      alert('Booking cancelled successfully' + (cancellationNote ? ' and notification sent' : ''));
      setShowCancelModal(false);
      setCancellingBooking(null);
      setCancellationNote('');
      await loadAllBookings();
      await loadTimeSlots();
    } else {
      alert('Failed to cancel booking');
    }
  };

  const handleAddSelfToAllSlots = async () => {
    const adminUserId = currentUser?.uid;
    if (!adminUserId) {
      alert('Unable to get user ID. Please make sure you are logged in.');
      return;
    }

    // Check if current user is marked as a session host
    const isCurrentUserHost = hosts.some(h => h.id === adminUserId);
    if (!isCurrentUserHost) {
      alert('You are not marked as a Session Host yet. Please click "Create My Profile" first.');
      return;
    }

    if (window.confirm('Add yourself as available for all existing time slots?')) {
      console.log('Adding user to all slots, user ID:', adminUserId);

      const result = await addCurrentUserToAllSlots(adminUserId);
      if (result.success) {
        alert(`You have been added to ${result.count} time slots. Reloading availability...`);
        // Reload both hosts and availability to see updated data
        await loadHosts();
        await loadHostAvailability();
        await loadTimeSlots(); // Reload slots to update counts
        console.log('Availability reloaded successfully');
      } else {
        alert('Failed to add you to slots: ' + result.error);
        console.error('Error adding to slots:', result.error);
      }
    }
  };

  const handleRunDiagnostic = async () => {
    setRunningDiagnostic(true);
    try {
      const result = await diagnoseAvailabilityIdMismatches(allUsers);
      if (result.success) {
        setDiagnosticResults(result);
        console.log('Diagnostic results:', result);
      } else {
        alert('Diagnostic failed: ' + result.error);
      }
    } catch (error) {
      console.error('Error running diagnostic:', error);
      alert('Diagnostic failed: ' + error.message);
    } finally {
      setRunningDiagnostic(false);
    }
  };

  const handleMigrateAvailability = async () => {
    if (!diagnosticResults || diagnosticResults.mismatches.length === 0) {
      alert('No mismatches to migrate');
      return;
    }

    const confirmMsg = `This will migrate ${diagnosticResults.mismatches.length} availability records from Auth UIDs to Firestore document IDs. Continue?`;
    if (!window.confirm(confirmMsg)) {
      return;
    }

    setRunningMigration(true);
    try {
      const result = await migrateAvailabilityIds(diagnosticResults.mismatches);
      if (result.success) {
        alert(`Migration complete! ${result.migrated} records migrated successfully.${result.failed > 0 ? ` ${result.failed} records failed.` : ''}`);

        // Re-run diagnostic to confirm
        await handleRunDiagnostic();

        // Reload availability
        await loadHostAvailability();
        await loadTimeSlots();
      } else {
        alert('Migration failed: ' + result.error);
      }
    } catch (error) {
      console.error('Error running migration:', error);
      alert('Migration failed: ' + error.message);
    } finally {
      setRunningMigration(false);
    }
  };

  const handleDeleteOrphaned = async () => {
    if (!diagnosticResults || diagnosticResults.orphaned.length === 0) {
      alert('No orphaned records to delete');
      return;
    }

    const confirmMsg = `This will permanently delete ${diagnosticResults.orphaned.length} orphaned availability record${diagnosticResults.orphaned.length > 1 ? 's' : ''} that don't match any user. This cannot be undone. Continue?`;
    if (!window.confirm(confirmMsg)) {
      return;
    }

    setDeletingOrphaned(true);
    try {
      const result = await deleteOrphanedAvailability(diagnosticResults.orphaned);
      if (result.success) {
        alert(`Cleanup complete! ${result.deleted} orphaned record${result.deleted > 1 ? 's' : ''} deleted.${result.failed > 0 ? ` ${result.failed} records failed.` : ''}`);

        // Re-run diagnostic to confirm
        await handleRunDiagnostic();

        // Reload availability
        await loadHostAvailability();
        await loadTimeSlots();
      } else {
        alert('Deletion failed: ' + result.error);
      }
    } catch (error) {
      console.error('Error deleting orphaned records:', error);
      alert('Deletion failed: ' + error.message);
    } finally {
      setDeletingOrphaned(false);
    }
  };

  const handlePreviousWeek = () => {
    setCurrentAvailabilityWeek(prev => {
      const newDate = new Date(prev);
      newDate.setDate(newDate.getDate() - 7);
      return newDate;
    });
  };

  const handleNextWeek = () => {
    setCurrentAvailabilityWeek(prev => {
      const newDate = new Date(prev);
      newDate.setDate(newDate.getDate() + 7);
      return newDate;
    });
  };

  const handleTodayWeek = () => {
    setCurrentAvailabilityWeek(new Date());
  };

  const renderCalendar = () => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(monthStart);
    const startDate = startOfWeek(monthStart);
    const endDate = endOfWeek(monthEnd);

    const rows = [];
    let days = [];
    let day = startDate;

    while (day <= endDate) {
      for (let i = 0; i < 7; i++) {
        const formattedDate = format(day, 'd');
        const cloneDay = day;
        const daySlots = getSlotsForDate(day);

        // Calculate availability based on unbooked hosts
        const hasAvailableSlots = daySlots.some(slot => {
          const unbookedHosts = getAvailableUnbookedHosts(slot.id);
          return unbookedHosts.length > 0 || slot.capacity - slot.bookingCount > 0;
        });
        const isFullyBooked = daySlots.length > 0 && !hasAvailableSlots;
        const isPast = day < new Date() && !isSameDay(day, new Date());

        // Get unique hosts for this day (both available and booked)
        const dayUnbookedHostIds = new Set();
        const dayBookedHostIds = new Set();
        daySlots.forEach(slot => {
          const unbookedHosts = getAvailableUnbookedHosts(slot.id);
          const bookedHosts = getBookedHosts(slot.id);
          unbookedHosts.forEach(host => dayUnbookedHostIds.add(host.id));
          bookedHosts.forEach(host => dayBookedHostIds.add(host.id));
        });
        const dayUnbookedHosts = hosts.filter(h => dayUnbookedHostIds.has(h.id));
        const dayBookedHosts = hosts.filter(h => dayBookedHostIds.has(h.id) && !dayUnbookedHostIds.has(h.id));

        days.push(
          <div
            className={`appt-calendar-day ${
              !isSameMonth(day, monthStart) ? 'disabled' : ''
            } ${isSameDay(day, new Date()) ? 'today' : ''} ${
              hasAvailableSlots ? 'available' : ''
            } ${isFullyBooked ? 'fully-booked' : ''} ${isPast ? 'past' : ''}`}
            key={day}
            onClick={() => !isPast && handleDateClick(cloneDay)}
          >
            <span className="day-number">{formattedDate}</span>
            {daySlots.length > 0 && (
              <div className="slot-indicator">
                {daySlots.length} slot{daySlots.length > 1 ? 's' : ''}
              </div>
            )}
            {(dayUnbookedHosts.length > 0 || dayBookedHosts.length > 0) && (
              <div className="host-initials-row">
                {dayUnbookedHosts.map(host => (
                  <div key={host.id} className="host-initial-circle available" title={`${host.name} - Available`} style={{ background: '#667eea' }}>
                    {host.initials || host.name.split(' ').map(n => n[0]).join('').substring(0, 2)}
                  </div>
                ))}
                {dayBookedHosts.map(host => (
                  <div key={host.id} className="host-initial-circle booked" title={`${host.name} - Booked`} style={{ background: '#dc3545' }}>
                    {host.initials || host.name.split(' ').map(n => n[0]).join('').substring(0, 2)}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
        day = addDays(day, 1);
      }
      rows.push(
        <div className="appt-calendar-row" key={day}>
          {days}
        </div>
      );
      days = [];
    }
    return <div className="appt-calendar-grid">{rows}</div>;
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  if (loading) {
    return (
      <div className="appointments-container">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading appointments...</p>
        </div>
      </div>
    );
  }

  const availableSlots = selectedDate ? getSlotsForDate(selectedDate) : [];

  return (
    <div className="appointments-container">
      <header className="appointments-header">
        <div className="appointments-header-content">
          <div className="header-left">
            <button onClick={() => navigate('/')} className="back-button">
              ← Home
            </button>
            <div className="header-title-section">
              <h1>Tutorial Booking</h1>
              <p className="subtitle">Request a work session with Mr. Willeby</p>
            </div>
          </div>
          <div className="header-right">
            {currentUser && (
              <span style={{ marginRight: '1rem', fontSize: '0.85rem', color: '#666' }}>
                Logged in as: {currentUser.email} {isAdmin && '(Admin)'}
              </span>
            )}
            {currentUser ? (
              <button onClick={handleLogout} className="logout-button">Logout</button>
            ) : (
              <button onClick={() => navigate('/')} className="login-button">Login</button>
            )}
          </div>
        </div>
      </header>

      <div className="tabs">
        <button
          className={`tab ${activeTab === 'book' ? 'active' : ''}`}
          onClick={() => setActiveTab('book')}
        >
          {isAdmin ? 'Session Calendar' : 'Book a Session'}
        </button>

        {/* My Sessions tab - visible to all users */}
        <button
          className={`tab ${activeTab === 'mySessions' ? 'active' : ''}`}
          onClick={() => setActiveTab('mySessions')}
        >
          My Sessions ({mySessionsBookings.length})
        </button>

        {/* My Availability tab - visible to session hosts */}
        {currentUserData?.permissions?.sessionHost && (
          <button
            className={`tab ${activeTab === 'myAvailability' ? 'active' : ''}`}
            onClick={() => setActiveTab('myAvailability')}
          >
            My Availability
          </button>
        )}

        {!isAdmin && (
          <button
            className={`tab ${activeTab === 'request' ? 'active' : ''}`}
            onClick={() => setActiveTab('request')}
          >
            Request a Time
          </button>
        )}
        {isAdmin && (
          <>
            <button
              className={`tab ${activeTab === 'bookings' ? 'active' : ''}`}
              onClick={() => setActiveTab('bookings')}
            >
              All Bookings ({allBookings.length})
            </button>
            <button
              className={`tab ${activeTab === 'requests' ? 'active' : ''}`}
              onClick={() => setActiveTab('requests')}
            >
              Pending Requests ({allRequests.filter(r => r.status === 'pending').length})
            </button>
            <button
              className={`tab ${activeTab === 'hosts' ? 'active' : ''}`}
              onClick={() => setActiveTab('hosts')}
            >
              Session Hosts ({hosts.length})
            </button>
            <button
              className="tab add-slot-tab"
              onClick={() => setShowAdminModal(true)}
            >
              + Add Time Slot
            </button>
            <button
              className="tab clear-slots-tab"
              onClick={handleClearAllSlots}
              style={{ background: '#f56565', color: 'white' }}
            >
              🗑️ Clear All Slots
            </button>
          </>
        )}
      </div>

      {activeTab === 'mySessions' ? (
        <div className="admin-section">
          <h3>My Sessions</h3>
          <p className="section-description">Sessions where you are the host or the client</p>
          {mySessionsBookings.length === 0 ? (
            <p className="no-data">You have no booked sessions</p>
          ) : (
            <>
              {(() => {
                const now = new Date();
                const today = format(now, 'yyyy-MM-dd');
                const currentTime = format(now, 'HH:mm');

                const upcomingSessions = mySessionsBookings.filter(booking => {
                  if (booking.date > today) return true;
                  if (booking.date === today) return booking.endTime > currentTime;
                  return false;
                });

                const pastSessions = mySessionsBookings.filter(booking => {
                  if (booking.date < today) return true;
                  if (booking.date === today) return booking.endTime <= currentTime;
                  return false;
                });

                return (
                  <>
                    {upcomingSessions.length > 0 && (
                      <>
                        <h4 style={{ marginTop: '1rem', color: '#059669', borderBottom: '2px solid #059669', paddingBottom: '0.5rem' }}>
                          ⏰ Upcoming Sessions ({upcomingSessions.length})
                        </h4>
                        <div className="bookings-list">
                          {upcomingSessions.map(booking => {
                            const bookingHost = booking.hostId ? hosts.find(h => h.id === booking.hostId) : null;
                            const isHost = booking.hostId === currentUserData?.id;
                            const isClient = booking.email?.toLowerCase() === currentUser?.email?.toLowerCase();

                            return (
                              <div key={booking.id} className={`booking-card ${isHost ? 'as-host' : 'as-client'}`}>
                                <div className="booking-header">
                                  <div>
                                    <strong>{booking.name}</strong>
                                    <span className="booking-email">{booking.email}</span>
                                    {isHost && <span className="role-badge host-badge">You're Hosting</span>}
                                    {isClient && !isHost && <span className="role-badge client-badge">You're Attending</span>}
                                  </div>
                                  <button
                                    onClick={() => {
                                      setCancellingBooking(booking);
                                      setShowCancelModal(true);
                                    }}
                                    className="cancel-session-btn"
                                    title="Cancel this session"
                                  >
                                    Cancel
                                  </button>
                                </div>
                                <div className="booking-details">
                                  <div>📅 {booking.date}</div>
                                  <div>🕐 {formatTime(booking.startTime)} - {formatTime(booking.endTime)}</div>
                                  {booking.room && <div>🚪 Room {booking.room}</div>}
                                  {bookingHost && !isHost && (
                                    <div>👤 Host: {bookingHost.name}</div>
                                  )}
                                  {isHost && (
                                    <div>👤 Client: {booking.name}</div>
                                  )}
                                  {booking.reason && <div>📝 {booking.reason}</div>}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </>
                    )}

                    {pastSessions.length > 0 && (
                      <>
                        <h4 style={{ marginTop: '1.5rem', color: '#6b7280', borderBottom: '2px solid #6b7280', paddingBottom: '0.5rem' }}>
                          📜 Past Sessions ({pastSessions.length})
                        </h4>
                        <div className="bookings-list" style={{ opacity: 0.7 }}>
                          {pastSessions.map(booking => {
                            const bookingHost = booking.hostId ? hosts.find(h => h.id === booking.hostId) : null;
                            const isHost = booking.hostId === currentUserData?.id;
                            const isClient = booking.email?.toLowerCase() === currentUser?.email?.toLowerCase();

                            return (
                              <div key={booking.id} className={`booking-card ${isHost ? 'as-host' : 'as-client'}`} style={{ background: '#f9fafb' }}>
                                <div className="booking-header">
                                  <div>
                                    <strong>{booking.name}</strong>
                                    <span className="booking-email">{booking.email}</span>
                                    {isHost && <span className="role-badge host-badge">You Hosted</span>}
                                    {isClient && !isHost && <span className="role-badge client-badge">You Attended</span>}
                                  </div>
                                </div>
                                <div className="booking-details">
                                  <div>📅 {booking.date}</div>
                                  <div>🕐 {formatTime(booking.startTime)} - {formatTime(booking.endTime)}</div>
                                  {booking.room && <div>🚪 Room {booking.room}</div>}
                                  {bookingHost && !isHost && (
                                    <div>👤 Host: {bookingHost.name}</div>
                                  )}
                                  {isHost && (
                                    <div>👤 Client: {booking.name}</div>
                                  )}
                                  {booking.reason && <div>📝 {booking.reason}</div>}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </>
                    )}
                  </>
                );
              })()}
            </>
          )}
        </div>
      ) : activeTab === 'myAvailability' ? (
        <div className="admin-section">
          <h3>My Availability</h3>
          <p className="section-description">Select the time slots when you are available for sessions</p>

          {/* Debug info - remove after fixing Sara's issue */}
          {currentUserData && (
            <div style={{
              background: '#f0f9ff',
              border: '1px solid #bae6fd',
              borderRadius: '8px',
              padding: '12px',
              marginBottom: '16px',
              fontSize: '0.875rem',
              fontFamily: 'monospace'
            }}>
              <div><strong>Debug Info:</strong></div>
              <div>Name: {currentUserData.name}</div>
              <div>Firestore ID: {currentUserData.id}</div>
              <div>Auth UID: {currentUser?.uid}</div>
              <div>Selected Slots: {myAvailability.size}</div>
            </div>
          )}

          {timeSlots.length === 0 ? (
            <div className="empty-state">
              <h4>No Time Slots Available</h4>
              <p>Contact an administrator to add session time slots.</p>
            </div>
          ) : (
            <div className="availability-list">
              {groupSlotsByDate().map((group, idx) => (
                <div key={idx} className="date-group">
                  <h4 className="date-header">
                    {format(group.date, 'EEEE, MMMM d, yyyy')}
                  </h4>
                  <div className="availability-slots-grid">
                    {group.slots.map(slot => {
                      const isAvailable = myAvailability.has(slot.id);
                      return (
                        <div
                          key={slot.id}
                          className={`availability-slot-card ${isAvailable ? 'available' : 'unavailable'}`}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (!savingAvailability) {
                              handleToggleMyAvailability(slot.id);
                            }
                          }}
                          style={{ cursor: savingAvailability ? 'not-allowed' : 'pointer' }}
                        >
                          <div className="slot-time">
                            {formatTime(slot.startTime)} - {formatTime(slot.endTime)}
                          </div>
                          <div className="slot-status">
                            {isAvailable ? (
                              <>
                                <span className="status-icon">✓</span>
                                <span className="status-text">Available</span>
                              </>
                            ) : (
                              <>
                                <span className="status-icon">○</span>
                                <span className="status-text">Unavailable</span>
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
          {savingAvailability && (
            <div className="saving-indicator">
              <div className="spinner-small"></div>
              <span>Saving...</span>
            </div>
          )}
        </div>
      ) : activeTab === 'bookings' ? (
        <div className="admin-section">
          <h3>All Bookings</h3>
          {allBookings.length === 0 ? (
            <p className="no-data">No bookings yet</p>
          ) : (
            <div className="bookings-list">
              {allBookings.map(booking => {
                const bookingHost = booking.hostId ? hosts.find(h => h.id === booking.hostId) : null;
                return (
                  <div key={booking.id} className="booking-card">
                    <div className="booking-header">
                      <strong>{booking.name}</strong>
                      <span className="booking-email">{booking.email}</span>
                    </div>
                    <div className="booking-details">
                      <div>📅 {booking.date}</div>
                      <div>🕐 {formatTime(booking.startTime)} - {formatTime(booking.endTime)}</div>
                      {booking.room && <div>🚪 Room {booking.room}</div>}
                      {bookingHost && (
                        <div>👤 Host: {bookingHost.name}</div>
                      )}
                      {booking.reason && <div>📝 {booking.reason}</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ) : activeTab === 'requests' ? (
        <div className="admin-section">
          <h3>Appointment Requests</h3>
          {allRequests.length === 0 ? (
            <p className="no-data">No requests yet</p>
          ) : (
            <div className="requests-list">
              {allRequests.map(request => (
                <div key={request.id} className={`request-card ${request.status}`}>
                  <div className="request-header">
                    <strong>{request.name}</strong>
                    <span className={`status-badge ${request.status}`}>
                      {request.status}
                    </span>
                  </div>
                  <div className="request-details">
                    <div>✉️ {request.email}</div>
                    <div>📅 {request.date}</div>
                    <div>🕐 {request.time}</div>
                    <div>📝 {request.reason}</div>
                  </div>
                  {request.status === 'pending' && (
                    <div className="request-actions">
                      <button
                        onClick={() => handleApproveRequest(request.id)}
                        className="approve-button"
                      >
                        ✓ Approve
                      </button>
                      <button
                        onClick={() => handleDenyRequest(request.id)}
                        className="deny-button"
                      >
                        ✗ Deny
                      </button>
                    </div>
                  )}
                  <button
                    onClick={() => handleDeleteRequest(request.id)}
                    className="delete-request-button"
                  >
                    Delete
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : activeTab === 'hosts' ? (
        <div className="admin-section">
          <div className="hosts-header">
            <h3>Session Hosts</h3>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              <button onClick={handleAddSelfToAllSlots} className="add-host-button" style={{ background: '#667eea' }}>
                ➕ Add Myself to All Slots
              </button>
              <button onClick={() => navigate('/users')} className="add-host-button">
                👥 Manage Hosts in Users Page
              </button>
              <button onClick={handleRunDiagnostic} className="add-host-button" disabled={runningDiagnostic} style={{ background: '#f59e0b' }}>
                {runningDiagnostic ? '🔍 Running...' : '🔍 Run Diagnostic'}
              </button>
            </div>
          </div>

          <div className="info-banner">
            <p>💡 <strong>Session Host Management:</strong> New time slots you create will automatically include you as available.</p>
            <p>⚡ Click "Add Myself to All Slots" to add yourself to all existing time slots. Manage other session hosts on the Users page.</p>
          </div>

          {diagnosticResults && (
            <div style={{
              background: diagnosticResults.summary.mismatches > 0 ? '#fef3c7' : '#d1fae5',
              border: `2px solid ${diagnosticResults.summary.mismatches > 0 ? '#f59e0b' : '#10b981'}`,
              borderRadius: '12px',
              padding: '1.5rem',
              marginBottom: '1.5rem'
            }}>
              <h4 style={{ marginTop: 0 }}>
                {diagnosticResults.summary.mismatches > 0 ? '⚠️ Availability ID Diagnostic Results' : '✅ Availability ID Diagnostic Results'}
              </h4>

              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                gap: '1rem',
                marginBottom: '1rem'
              }}>
                <div style={{ background: 'white', padding: '1rem', borderRadius: '8px' }}>
                  <div style={{ fontSize: '0.875rem', color: '#666' }}>Total Records</div>
                  <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>{diagnosticResults.summary.total}</div>
                </div>
                <div style={{ background: 'white', padding: '1rem', borderRadius: '8px' }}>
                  <div style={{ fontSize: '0.875rem', color: '#10b981' }}>✓ Correct</div>
                  <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#10b981' }}>{diagnosticResults.summary.correct}</div>
                </div>
                <div style={{ background: 'white', padding: '1rem', borderRadius: '8px' }}>
                  <div style={{ fontSize: '0.875rem', color: '#f59e0b' }}>⚠ Mismatches</div>
                  <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#f59e0b' }}>{diagnosticResults.summary.mismatches}</div>
                </div>
                <div style={{ background: 'white', padding: '1rem', borderRadius: '8px' }}>
                  <div style={{ fontSize: '0.875rem', color: '#ef4444' }}>❌ Orphaned</div>
                  <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#ef4444' }}>{diagnosticResults.summary.orphaned}</div>
                </div>
              </div>

              {diagnosticResults.summary.mismatches > 0 && (
                <>
                  <div style={{ marginBottom: '1rem' }}>
                    <strong>Mismatched Records (using Auth UID instead of Firestore ID):</strong>
                    <div style={{
                      maxHeight: '300px',
                      overflowY: 'auto',
                      background: 'white',
                      padding: '1rem',
                      borderRadius: '8px',
                      marginTop: '0.5rem',
                      fontSize: '0.875rem'
                    }}>
                      {diagnosticResults.mismatches.map((mismatch, idx) => (
                        <div key={idx} style={{
                          padding: '0.5rem',
                          borderBottom: '1px solid #e5e7eb',
                          fontFamily: 'monospace'
                        }}>
                          <div><strong>{mismatch.userName}</strong> ({mismatch.userEmail})</div>
                          <div style={{ color: '#666', fontSize: '0.8rem' }}>
                            Current ID: {mismatch.currentHostId.substring(0, 20)}...
                          </div>
                          <div style={{ color: '#10b981', fontSize: '0.8rem' }}>
                            Correct ID: {mismatch.correctHostId.substring(0, 20)}...
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <button
                    onClick={handleMigrateAvailability}
                    disabled={runningMigration}
                    style={{
                      background: '#10b981',
                      color: 'white',
                      padding: '0.75rem 1.5rem',
                      borderRadius: '8px',
                      border: 'none',
                      cursor: runningMigration ? 'not-allowed' : 'pointer',
                      fontWeight: 'bold',
                      fontSize: '1rem'
                    }}
                  >
                    {runningMigration ? '⚙️ Migrating...' : `✓ Fix ${diagnosticResults.summary.mismatches} Mismatch${diagnosticResults.summary.mismatches > 1 ? 'es' : ''}`}
                  </button>
                </>
              )}

              {diagnosticResults.summary.orphaned > 0 && (
                <div style={{ marginTop: '1rem' }}>
                  <strong>⚠️ Orphaned Records (no matching user):</strong>
                  <div style={{
                    maxHeight: '300px',
                    overflowY: 'auto',
                    background: 'white',
                    padding: '1rem',
                    borderRadius: '8px',
                    marginTop: '0.5rem',
                    fontSize: '0.875rem'
                  }}>
                    {diagnosticResults.orphaned.map((orphan, idx) => {
                      // Find the time slot info for this orphaned record
                      const slot = timeSlots.find(s => s.id === orphan.slotId);
                      return (
                        <div key={idx} style={{
                          padding: '0.5rem',
                          borderBottom: '1px solid #e5e7eb',
                          fontFamily: 'monospace'
                        }}>
                          <div style={{ color: '#ef4444', fontWeight: 'bold' }}>Record {idx + 1}</div>
                          <div style={{ color: '#666', fontSize: '0.8rem', marginTop: '0.25rem' }}>
                            <div>Host ID: {orphan.hostId}</div>
                            <div>Slot ID: {orphan.slotId.substring(0, 20)}...</div>
                            {slot && (
                              <div style={{ color: '#059669', marginTop: '0.25rem' }}>
                                Time Slot: {slot.date} at {formatTime(slot.startTime)} - {formatTime(slot.endTime)}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div style={{ fontSize: '0.875rem', color: '#666', marginTop: '0.5rem' }}>
                    💡 These records reference host IDs that don't match any user in your system. They may be from deleted users or data migration issues. Review and delete if no longer needed.
                  </div>
                  <button
                    onClick={handleDeleteOrphaned}
                    disabled={deletingOrphaned}
                    style={{
                      background: '#ef4444',
                      color: 'white',
                      padding: '0.75rem 1.5rem',
                      borderRadius: '8px',
                      border: 'none',
                      cursor: deletingOrphaned ? 'not-allowed' : 'pointer',
                      fontWeight: 'bold',
                      fontSize: '1rem',
                      marginTop: '0.75rem'
                    }}
                  >
                    {deletingOrphaned ? '🗑️ Deleting...' : `🗑️ Delete ${diagnosticResults.summary.orphaned} Orphaned Record${diagnosticResults.summary.orphaned > 1 ? 's' : ''}`}
                  </button>
                </div>
              )}

              {diagnosticResults.summary.mismatches === 0 && diagnosticResults.summary.orphaned === 0 && (
                <div style={{ color: '#10b981', fontWeight: 'bold' }}>
                  ✅ All availability records are using correct Firestore document IDs!
                </div>
              )}
            </div>
          )}

          {hosts.length === 0 ? (
            <p className="no-data">No session hosts yet. Go to the Users page to add hosts.</p>
          ) : (
            <div className="hosts-section-content">
              <div className="hosts-list">
                <h4>Current Session Hosts</h4>
                {hosts.map(host => (
                  <div key={host.id} className="host-card">
                    <div className="host-avatar">
                      {host.initials || host.name.split(' ').map(n => n[0]).join('').substring(0, 2)}
                    </div>
                    <div className="host-info">
                      <div className="host-name">{host.name}</div>
                      <div className="host-email">{host.email}</div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="availability-section">
                <h4>Host Availability Calendar</h4>
                <p className="availability-help">Select hosts for each time slot (Monday-Friday only)</p>

                {timeSlots.length === 0 ? (
                  <p className="no-data">Create time slots first to set host availability</p>
                ) : (
                  <div className="availability-calendar-container">
                    {/* Week navigation controls */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginBottom: '1rem',
                      padding: '1rem',
                      background: '#f8f9fa',
                      borderRadius: '8px'
                    }}>
                      <button
                        onClick={handlePreviousWeek}
                        style={{
                          padding: '0.5rem 1rem',
                          background: '#667eea',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontWeight: 'bold'
                        }}
                      >
                        ← Previous Week
                      </button>
                      <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                        <button
                          onClick={handleTodayWeek}
                          style={{
                            padding: '0.5rem 1rem',
                            background: '#10b981',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontWeight: 'bold'
                          }}
                        >
                          This Week
                        </button>
                        <span style={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                          {format(startOfWeek(currentAvailabilityWeek, { weekStartsOn: 1 }), 'MMM d')} - {format(endOfWeek(currentAvailabilityWeek, { weekStartsOn: 1 }), 'MMM d, yyyy')}
                        </span>
                      </div>
                      <button
                        onClick={handleNextWeek}
                        style={{
                          padding: '0.5rem 1rem',
                          background: '#667eea',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontWeight: 'bold'
                        }}
                      >
                        Next Week →
                      </button>
                    </div>

                    {/* Group slots by week and day */}
                    {(() => {
                      // Get the week start and end for the current availability week
                      const currentWeekStart = startOfWeek(currentAvailabilityWeek, { weekStartsOn: 1 });
                      const currentWeekEnd = endOfWeek(currentAvailabilityWeek, { weekStartsOn: 1 });

                      // Filter to only show weekday slots in the current week
                      const weekdaySlots = timeSlots.filter(slot => {
                        const date = new Date(slot.date + 'T00:00:00');
                        const day = date.getDay();

                        // Check if it's a weekday (Monday-Friday)
                        if (day < 1 || day > 5) return false;

                        // Check if it's in the current week
                        return date >= currentWeekStart && date <= currentWeekEnd;
                      });

                      // Group slots by week
                      const weekGroups = {};
                      weekdaySlots.forEach(slot => {
                        const date = new Date(slot.date + 'T00:00:00');
                        const weekStart = startOfWeek(date, { weekStartsOn: 1 }); // Start on Monday
                        const weekKey = format(weekStart, 'yyyy-MM-dd');

                        if (!weekGroups[weekKey]) {
                          weekGroups[weekKey] = {
                            weekStart,
                            days: { Mon: [], Tue: [], Wed: [], Thu: [], Fri: [] }
                          };
                        }

                        const dayName = format(date, 'EEE');
                        if (weekGroups[weekKey].days[dayName]) {
                          weekGroups[weekKey].days[dayName].push(slot);
                        }
                      });

                      // If no slots in current week, show empty message
                      if (Object.keys(weekGroups).length === 0) {
                        return (
                          <div style={{
                            padding: '2rem',
                            textAlign: 'center',
                            color: '#666',
                            background: '#f8f9fa',
                            borderRadius: '8px'
                          }}>
                            No time slots for this week. Use the navigation to view other weeks or create new time slots.
                          </div>
                        );
                      }

                      return Object.entries(weekGroups).map(([weekKey, weekData]) => (
                        <div key={weekKey} className="availability-week-block">
                          <div className="availability-week-header">
                            Week of {format(weekData.weekStart, 'MMM d, yyyy')}
                          </div>

                          <div className="availability-calendar-grid">
                            {/* Day headers */}
                            <div className="availability-day-headers">
                              {['Mon', 'Tue', 'Wed', 'Thu', 'Fri'].map(day => (
                                <div key={day} className="availability-day-header">{day}</div>
                              ))}
                            </div>

                            {/* Day columns */}
                            <div className="availability-days-container">
                              {['Mon', 'Tue', 'Wed', 'Thu', 'Fri'].map(day => (
                                <div key={day} className="availability-day-column">
                                  {weekData.days[day].length === 0 ? (
                                    <div className="no-slots-day">No slots</div>
                                  ) : (
                                    weekData.days[day].map(slot => (
                                      <div key={slot.id} className="availability-slot-card">
                                        <div className="availability-slot-time">
                                          {formatTime(slot.startTime)}
                                        </div>
                                        <div className="availability-hosts-list">
                                          {hosts.map(host => {
                                            const isAvailable = availableHostsMap[slot.id]?.includes(host.id);
                                            return (
                                              <div
                                                key={host.id}
                                                className="availability-host-option"
                                                onClick={() => handleToggleHostAvailability(host.id, slot.id, isAvailable)}
                                              >
                                                <input
                                                  type="checkbox"
                                                  checked={isAvailable || false}
                                                  readOnly
                                                />
                                                <span className="host-option-initials">
                                                  {host.initials || host.name.split(' ').map(n => n[0]).join('').substring(0, 2)}
                                                </span>
                                              </div>
                                            );
                                          })}
                                        </div>
                                      </div>
                                    ))
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      ));
                    })()}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      ) : activeTab === 'book' ? (
        <div className="booking-section">
          <div className="calendar-section">
            <div className="calendar-controls">
              <button onClick={() => setCurrentDate(addMonths(currentDate, -1))}>←</button>
              <h3>{format(currentDate, 'MMMM yyyy')}</h3>
              <button onClick={() => setCurrentDate(addMonths(currentDate, 1))}>→</button>
            </div>

            <div className="appt-calendar-weekdays">
              <div className="weekday">Sun</div>
              <div className="weekday">Mon</div>
              <div className="weekday">Tue</div>
              <div className="weekday">Wed</div>
              <div className="weekday">Thu</div>
              <div className="weekday">Fri</div>
              <div className="weekday">Sat</div>
            </div>

            {renderCalendar()}

            <div className="legend">
              <div className="legend-item">
                <div className="legend-box available"></div>
                <span>Available</span>
              </div>
              <div className="legend-item">
                <div className="legend-box fully-booked"></div>
                <span>Fully Booked</span>
              </div>
              <div className="legend-item">
                <div className="legend-box today"></div>
                <span>Today</span>
              </div>
            </div>
          </div>

          <div className="slots-panel">
            <div className="slots-panel-header">
              <div className="month-filter">
                <button onClick={() => {
                  setFilterMonth(addMonths(filterMonth, -1));
                  setFilterWeek('all');
                }} className="month-nav-btn">←</button>
                <h3>{format(filterMonth, 'MMMM yyyy')}</h3>
                <button onClick={() => {
                  setFilterMonth(addMonths(filterMonth, 1));
                  setFilterWeek('all');
                }} className="month-nav-btn">→</button>
              </div>
              <div className="filters-row">
                <div className="time-filter">
                  <label htmlFor="week-filter">Week:</label>
                  <select
                    id="week-filter"
                    value={filterWeek}
                    onChange={(e) => setFilterWeek(e.target.value)}
                    className="time-filter-select"
                  >
                    <option value="all">All Weeks</option>
                    {getWeeksInMonth(filterMonth).map(week => (
                      <option key={week.number} value={week.number}>
                        {week.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="time-filter">
                  <label htmlFor="time-filter">Time:</label>
                  <select
                    id="time-filter"
                    value={filterTime}
                    onChange={(e) => setFilterTime(e.target.value)}
                    className="time-filter-select"
                  >
                    <option value="all">All Times</option>
                    <option value="period7">7th Period (1:43 - 2:35 PM)</option>
                    <option value="slot1">2:45 - 3:30 PM</option>
                    <option value="slot2">3:30 - 4:15 PM</option>
                    <option value="slot3">4:15 - 5:00 PM</option>
                  </select>
                </div>
              </div>
            </div>
            {getSlotsForMonth(filterMonth).length === 0 ? (
              <p className="no-slots">No time slots for this month</p>
            ) : (
              <div className="slots-list">
                {getSlotsForMonth(filterMonth).map((slot, index, array) => {
                  const slotBookings = getBookingsForSlot(slot.id);
                  const unbookedHosts = getAvailableUnbookedHosts(slot.id);
                  const bookedHosts = getBookedHosts(slot.id);
                  const totalHostsForSlot = hosts.filter(h => availableHostsMap[slot.id]?.includes(h.id)).length;
                  const availableSpots = unbookedHosts.length > 0 ? unbookedHosts.length : slot.capacity - (slot.bookingCount || 0);
                  const isClickable = !isAdmin && availableSpots > 0;

                  // Check if this is a new day (different from previous slot)
                  const isNewDay = index === 0 || slot.date !== array[index - 1].date;

                  return (
                    <React.Fragment key={slot.id}>
                      {isNewDay && (
                        <div className="date-separator">
                          <div className="date-separator-line"></div>
                          <div className="date-separator-label">
                            {format(new Date(slot.date + 'T00:00:00'), 'EEEE, MMMM d, yyyy')}
                          </div>
                          <div className="date-separator-line"></div>
                        </div>
                      )}
                      <div
                        className={`slot-card ${availableSpots <= 0 ? 'full' : ''} ${isClickable ? 'clickable' : ''}`}
                        onClick={() => isClickable && handleSlotCardClick(slot)}
                      >
                      <div className="slot-card-layout">
                        <div className="slot-card-left">
                          <div className="slot-date">
                            {format(new Date(slot.date + 'T00:00:00'), 'EEE, MMM d, yyyy')}
                          </div>
                          <div className="slot-time">
                            {formatTime(slot.startTime)} - {formatTime(slot.endTime)}
                          </div>
                          {(unbookedHosts.length > 0 || bookedHosts.length > 0) && (
                            <div className="host-initials-row" style={{ marginTop: '0.5rem', justifyContent: 'flex-start' }}>
                              {unbookedHosts.map(host => (
                                <div key={host.id} className="host-initial-circle available" title={`${host.name} - Available`} style={{ background: '#667eea' }}>
                                  {host.initials || host.name.split(' ').map(n => n[0]).join('').substring(0, 2)}
                                </div>
                              ))}
                              {bookedHosts.map(host => (
                                <div key={host.id} className="host-initial-circle booked" title={`${host.name} - Booked`} style={{ background: '#dc3545' }}>
                                  {host.initials || host.name.split(' ').map(n => n[0]).join('').substring(0, 2)}
                                </div>
                              ))}
                            </div>
                          )}
                          {slot.notes && (
                            <div className="slot-notes">
                              📝 {slot.notes}
                            </div>
                          )}
                        </div>

                        <div className="slot-card-center">
                          {slotBookings.length > 0 ? (
                            <>
                              <div className="reserved-by-label">Reserved By:</div>
                              {slotBookings.map(booking => {
                                const bookingHost = booking.hostId ? hosts.find(h => h.id === booking.hostId) : null;
                                return (
                                  <div key={booking.id} className="reserved-name">
                                    <span>{booking.name}</span>
                                    {booking.room && (
                                      <span style={{ marginLeft: '0.5rem', fontSize: '0.85rem', color: '#48bb78', fontWeight: '600' }}>
                                        Room {booking.room}
                                      </span>
                                    )}
                                    {bookingHost && (
                                      <span style={{ marginLeft: '0.5rem', fontSize: '0.85rem', color: '#667eea' }}>
                                        with {bookingHost.initials || bookingHost.name.split(' ').map(n => n[0]).join('')}
                                      </span>
                                    )}
                                    {isAdmin && (
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleCancelBookingClick(booking);
                                        }}
                                        className="cancel-booking-btn-inline"
                                        title="Cancel booking"
                                      >
                                        ✗
                                      </button>
                                    )}
                                  </div>
                                );
                              })}
                            </>
                          ) : (
                            <div className="slot-status">
                              {totalHostsForSlot > 0 ? `${availableSpots} / ${totalHostsForSlot} spots available` : `${slot.capacity - slot.bookingCount} / ${slot.capacity} spots`}
                            </div>
                          )}
                        </div>

                        <div className="slot-card-right">
                          {slotBookings.some(b => b.status === 'cancelled') ? (
                            <div className="slot-cancelled">CANCELLED</div>
                          ) : (
                            <div className="slot-availability-vertical">
                              {totalHostsForSlot > 0 ? `${availableSpots} / ${totalHostsForSlot}` : `${slot.capacity - slot.bookingCount} / ${slot.capacity}`}
                            </div>
                          )}
                        </div>
                      </div>
                      {isAdmin && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteSlot(slot.id);
                          }}
                          className="delete-slot-btn"
                        >
                          ×
                        </button>
                      )}
                    </div>
                    </React.Fragment>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="request-section">
          <div className="request-form-container">
            <h3>Request a Custom Time</h3>
            <p>Can't find a suitable time? Request a custom appointment.</p>

            <form onSubmit={handleRequestSubmit}>
              <div className="form-group">
                <label htmlFor="req-name">Name</label>
                <input
                  type="text"
                  id="req-name"
                  value={requestForm.name}
                  onChange={(e) => setRequestForm({ ...requestForm, name: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="req-email">Email</label>
                <input
                  type="email"
                  id="req-email"
                  value={requestForm.email}
                  onChange={(e) => setRequestForm({ ...requestForm, email: e.target.value })}
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="req-date">Preferred Date</label>
                  <input
                    type="date"
                    id="req-date"
                    value={requestForm.date}
                    onChange={(e) => setRequestForm({ ...requestForm, date: e.target.value })}
                    min={format(new Date(), 'yyyy-MM-dd')}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="req-time">Preferred Time</label>
                  <input
                    type="time"
                    id="req-time"
                    value={requestForm.time}
                    onChange={(e) => setRequestForm({ ...requestForm, time: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="req-reason">Reason/Topic</label>
                <textarea
                  id="req-reason"
                  value={requestForm.reason}
                  onChange={(e) => setRequestForm({ ...requestForm, reason: e.target.value })}
                  placeholder="What would you like to discuss?"
                  rows="4"
                  required
                />
              </div>

              <button type="submit" className="submit-button">Submit Request</button>
            </form>
          </div>
        </div>
      )}

      {showBookingForm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <button className="modal-close" onClick={() => setShowBookingForm(false)}>×</button>
            <h2>Confirm Booking</h2>
            <p className="booking-details">
              {selectedSlot?.date} • {formatTime(selectedSlot?.startTime)} - {formatTime(selectedSlot?.endTime)}
            </p>

            <div className="booking-notice">
              ⚠️ For cancellations or changes, please contact Mr. Willeby directly.
            </div>

            <form onSubmit={handleBookingSubmit}>
              <div className="form-group">
                <label htmlFor="name">Student Name</label>
                <input
                  type="text"
                  id="name"
                  value={bookingForm.name}
                  onChange={(e) => setBookingForm({ ...bookingForm, name: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  value={bookingForm.email}
                  onChange={(e) => setBookingForm({ ...bookingForm, email: e.target.value })}
                  required
                />
              </div>

              {selectedSlot && availableHostsMap[selectedSlot.id]?.length > 0 && (
                <div className="form-group">
                  <label htmlFor="host">Select Session Host *</label>
                  <select
                    id="host"
                    value={selectedHost || ''}
                    onChange={(e) => setSelectedHost(e.target.value)}
                    required
                    className="host-select"
                  >
                    <option value="">-- Choose a host --</option>
                    {getAvailableUnbookedHosts(selectedSlot.id).map(host => (
                      <option key={host.id} value={host.id}>
                        {host.name} ({host.initials || host.name.split(' ').map(n => n[0]).join('')})
                      </option>
                    ))}
                  </select>
                  {getAvailableUnbookedHosts(selectedSlot.id).length === 0 && (
                    <p style={{ color: '#dc3545', fontSize: '0.9rem', marginTop: '0.5rem' }}>
                      All session hosts for this time slot are currently booked. Please select a different time.
                    </p>
                  )}
                </div>
              )}

              <div className="form-group">
                <label htmlFor="reason">Reason/Topic (Optional)</label>
                <textarea
                  id="reason"
                  value={bookingForm.reason}
                  onChange={(e) => setBookingForm({ ...bookingForm, reason: e.target.value })}
                  placeholder="What would you like to discuss?"
                  rows="3"
                />
              </div>

              <div className="modal-buttons">
                <button type="submit" className="confirm-button">Confirm Booking</button>
                <button type="button" onClick={() => setShowBookingForm(false)} className="cancel-button">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showAdminModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <button className="modal-close" onClick={() => setShowAdminModal(false)}>×</button>
            <h2>Create Time Slot{newSlotForm.isRepeating ? 's' : ''}</h2>

            <form onSubmit={handleCreateSlot}>
              <div className="form-group checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={newSlotForm.isRepeating}
                    onChange={(e) => setNewSlotForm({ ...newSlotForm, isRepeating: e.target.checked })}
                  />
                  <span>Repeat on specific days</span>
                </label>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="slot-date">{newSlotForm.isRepeating ? 'Start Date' : 'Date'}</label>
                  <input
                    type="date"
                    id="slot-date"
                    value={newSlotForm.date}
                    onChange={(e) => setNewSlotForm({ ...newSlotForm, date: e.target.value })}
                    min={format(new Date(), 'yyyy-MM-dd')}
                    required
                  />
                </div>

                {newSlotForm.isRepeating && (
                  <div className="form-group">
                    <label htmlFor="slot-end-date">End Date</label>
                    <input
                      type="date"
                      id="slot-end-date"
                      value={newSlotForm.endDate}
                      onChange={(e) => setNewSlotForm({ ...newSlotForm, endDate: e.target.value })}
                      min={newSlotForm.date || format(new Date(), 'yyyy-MM-dd')}
                      required={newSlotForm.isRepeating}
                    />
                  </div>
                )}
              </div>

              {newSlotForm.isRepeating && (
                <div className="form-group">
                  <label>Days of Week</label>
                  <div className="days-of-week-grid">
                    {Object.keys(newSlotForm.daysOfWeek).map(day => (
                      <label key={day} className="day-checkbox">
                        <input
                          type="checkbox"
                          checked={newSlotForm.daysOfWeek[day]}
                          onChange={(e) => setNewSlotForm({
                            ...newSlotForm,
                            daysOfWeek: {
                              ...newSlotForm.daysOfWeek,
                              [day]: e.target.checked
                            }
                          })}
                        />
                        <span>{day.charAt(0).toUpperCase() + day.slice(1, 3)}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="slot-start">Start Time</label>
                  <input
                    type="time"
                    id="slot-start"
                    value={newSlotForm.startTime}
                    onChange={(e) => setNewSlotForm({ ...newSlotForm, startTime: e.target.value })}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="slot-end">End Time</label>
                  <input
                    type="time"
                    id="slot-end"
                    value={newSlotForm.endTime}
                    onChange={(e) => setNewSlotForm({ ...newSlotForm, endTime: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="slot-notes">Notes (Optional)</label>
                <textarea
                  id="slot-notes"
                  value={newSlotForm.notes}
                  onChange={(e) => setNewSlotForm({ ...newSlotForm, notes: e.target.value })}
                  placeholder="Add any additional notes or details about this time slot..."
                  rows="3"
                />
              </div>

              <div className="modal-buttons">
                <button type="submit" className="confirm-button">
                  Create {newSlotForm.isRepeating ? 'Slots' : 'Slot'}
                </button>
                <button type="button" onClick={() => setShowAdminModal(false)} className="cancel-button">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showDateModal && selectedDate && (
        <div className="modal-overlay">
          <div className="modal-content">
            <button className="modal-close" onClick={() => setShowDateModal(false)}>×</button>
            <h2>{isAdmin ? 'Booking Summary' : 'Available Times'} - {format(selectedDate, 'MMMM d, yyyy')}</h2>

            {availableSlots.length === 0 ? (
              <p className="no-slots">No {isAdmin ? '' : 'available '}slots for this date</p>
            ) : isAdmin ? (
              // Admin view - show booking summary
              <div className="booking-summary-container">
                {availableSlots.map(slot => {
                  const slotBookings = getBookingsForSlot(slot.id);
                  const allHostsForSlot = hosts.filter(h => availableHostsMap[slot.id]?.includes(h.id));
                  const availableUnbookedHosts = getAvailableUnbookedHosts(slot.id);

                  return (
                    <div key={slot.id} className="summary-slot-card">
                      <div className="summary-slot-header">
                        <div className="slot-time-large">
                          {formatTime(slot.startTime)} - {formatTime(slot.endTime)}
                        </div>
                        <div className="slot-status-badge">
                          {slotBookings.length} / {allHostsForSlot.length > 0 ? allHostsForSlot.length : slot.capacity} booked
                        </div>
                      </div>

                      {allHostsForSlot.length > 0 && (
                        <>
                          <div className="available-hosts-summary">
                            <strong>Available Officers (Unbooked):</strong>
                            <div className="host-initials-row" style={{ marginTop: '0.5rem' }}>
                              {availableUnbookedHosts.map(host => (
                                <div key={host.id} className="host-initial-circle" title={host.name} style={{ background: '#667eea' }}>
                                  {host.initials || host.name.split(' ').map(n => n[0]).join('').substring(0, 2)}
                                </div>
                              ))}
                              {availableUnbookedHosts.length === 0 && <span style={{ color: '#999', fontSize: '0.9rem' }}>None - all officers are booked</span>}
                            </div>
                          </div>
                        </>
                      )}

                      {slotBookings.length > 0 ? (
                        <div className="bookings-summary">
                          <strong>Bookings:</strong>
                          {slotBookings.map(booking => {
                            const bookingHost = booking.hostId ? hosts.find(h => h.id === booking.hostId) : null;
                            return (
                              <div key={booking.id} className="summary-booking-item">
                                <div className="booking-student-info">
                                  <span className="student-name">{booking.name}</span>
                                  <span className="student-email">{booking.email}</span>
                                </div>
                                {booking.room && (
                                  <div className="booking-room-badge">
                                    🚪 Room {booking.room}
                                  </div>
                                )}
                                {bookingHost && (
                                  <div className="booking-host-badge">
                                    with {bookingHost.name}
                                  </div>
                                )}
                                {booking.reason && (
                                  <div className="booking-reason">📝 {booking.reason}</div>
                                )}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleCancelBookingClick(booking);
                                  }}
                                  className="cancel-booking-btn-inline"
                                  title="Cancel booking"
                                >
                                  ✗ Cancel
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="no-bookings-message">No bookings yet</div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              // Student view - show available slots to book
              <div className="modal-slots-list">
                {availableSlots.map(slot => {
                  const slotBookings = getBookingsForSlot(slot.id);
                  const unbookedHosts = getAvailableUnbookedHosts(slot.id);
                  const bookedHosts = getBookedHosts(slot.id);
                  const totalHostsForSlot = hosts.filter(h => availableHostsMap[slot.id]?.includes(h.id)).length;
                  const availableSpots = unbookedHosts.length > 0 ? unbookedHosts.length : slot.capacity - (slot.bookingCount || 0);
                  const isFull = availableSpots <= 0;

                  return (
                    <div
                      key={slot.id}
                      className={`slot-card ${isFull ? 'full' : ''} ${selectedSlot?.id === slot.id ? 'selected' : ''}`}
                      onClick={() => {
                        if (!isFull) {
                          handleSlotSelect(slot);
                          setShowDateModal(false);
                        }
                      }}
                    >
                      <div className="slot-time">
                        {formatTime(slot.startTime)} - {formatTime(slot.endTime)}
                      </div>
                      {(unbookedHosts.length > 0 || bookedHosts.length > 0) && (
                        <div className="host-initials-row" style={{ marginTop: '0.5rem', justifyContent: 'center' }}>
                          {unbookedHosts.map(host => (
                            <div key={host.id} className="host-initial-circle available" title={`${host.name} - Available`} style={{ background: '#667eea' }}>
                              {host.initials || host.name.split(' ').map(n => n[0]).join('').substring(0, 2)}
                            </div>
                          ))}
                          {bookedHosts.map(host => (
                            <div key={host.id} className="host-initial-circle booked" title={`${host.name} - Booked`} style={{ background: '#dc3545' }}>
                              {host.initials || host.name.split(' ').map(n => n[0]).join('').substring(0, 2)}
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="slot-availability">
                        {totalHostsForSlot > 0 ? `${availableSpots} / ${totalHostsForSlot} spots available` : `${slot.capacity - slot.bookingCount} / ${slot.capacity} spots available`}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {showCancelModal && cancellingBooking && (
        <div className="modal-overlay">
          <div className="modal-content">
            <button className="modal-close" onClick={() => setShowCancelModal(false)}>×</button>
            <h2>Cancel Booking</h2>
            <p>Cancel booking for <strong>{cancellingBooking.name}</strong>?</p>
            <p className="booking-details">
              {cancellingBooking.date} • {formatTime(cancellingBooking.startTime)} - {formatTime(cancellingBooking.endTime)}
            </p>

            <div className="form-group">
              <label htmlFor="cancel-note">Cancellation Note (Optional)</label>
              <textarea
                id="cancel-note"
                value={cancellationNote}
                onChange={(e) => setCancellationNote(e.target.value)}
                placeholder="Reason for cancellation..."
                rows="3"
              />
            </div>

            <div className="modal-buttons">
              <button onClick={handleConfirmCancel} className="confirm-button danger">
                Cancel Booking
              </button>
              <button onClick={() => setShowCancelModal(false)} className="cancel-button">
                Keep Booking
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default Appointments;
